import { InviteLock } from "@/components/growth/InviteLock";
import { openShare } from "@/lib/share";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Trophy, Sparkles, Share2, RotateCcw, X, Play, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TwoHeartsCanvas } from "@/components/twohearts/TwoHeartsGame";
import { UserAvatar } from "@/components/UserBits";
import { SkinShop, DuelPanel, skinColors } from "@/components/twohearts/THExtras";
import { MusicToggle } from "@/components/games/GameMusic";

const Board = ({ rows, testId, champion, empty = "Noch keine Rekorde – sei die oder der Erste!" }) => (
  <ol className="space-y-1.5" data-testid={testId}>
    {!rows?.length && <li className="text-sm text-white/60">{empty}</li>}
    {rows?.map((r, i) => (
      <li key={r.user.user_id} className="flex items-center gap-3 rounded-2xl bg-white/10 px-3 py-2" data-testid={`${testId}-row-${i}`}>
        <span className={`w-6 text-center font-black ${i === 0 ? "text-amber-300" : "text-white/70"}`}>{i === 0 ? <Crown className="w-4 h-4 inline" /> : i + 1}</span>
        <UserAvatar name={r.user.name} picture={r.user.picture} userId={r.user.user_id} size="w-8 h-8" text="text-xs" noStory />
        <span className="flex-1 min-w-0 truncate font-bold text-white">{r.user.name}</span>
        {champion === r.user.user_id && <span className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black px-1.5 py-px align-middle" data-testid={`${testId}-champion`}><Crown className="w-3 h-3" /> Champion</span>}
        <span className="font-black text-pink-200 tabular-nums">{r.score}</span>
      </li>
    ))}
  </ol>
);

const shareLink = (code) => `${window.location.origin}/zwei-herzen?ref=${code || ""}`;

function Guest() {
  const [sp] = useSearchParams();
  const ref = sp.get("ref") || "";
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/twohearts/public?ref=${encodeURIComponent(ref)}`).then(({ data }) => setD(data)).catch(() => setD({})); }, [ref]);
  const reg = `/registrieren?ref=${encodeURIComponent(ref)}&source=zwei-herzen`;
  useEffect(() => { sessionStorage.setItem("after_auth", "/zwei-herzen"); }, []);
  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[#2a0b2e]" data-testid="twohearts-guest">
      <div className="absolute inset-0 opacity-80"><TwoHeartsCanvas demo heartY={0.34} testId="twohearts-demo" /></div>
      <div className="relative z-10 min-h-[100dvh] flex flex-col justify-end p-5 pb-8 bg-gradient-to-t from-[#2a0b2e] via-[#2a0b2e]/80 to-transparent">
        <div className="max-w-md mx-auto w-full space-y-4">
          <p className="text-[11px] font-extrabold tracking-[0.25em] text-pink-300">ITZPLAY · SPIEL</p>
          <h1 className="hero-plain font-display text-5xl font-black text-white leading-none">Zwei <span className="text-pink-400">Herzen</span></h1>
          {d?.inviter ? (
            <div className="flex items-center gap-3 rounded-3xl bg-white/10 backdrop-blur p-3" data-testid="twohearts-challenge">
              <UserAvatar name={d.inviter.name} picture={d.inviter.picture} size="w-12 h-12" noStory />
              <p className="text-white text-sm"><b>{d.inviter.name}</b> fordert dich heraus{d.inviter.best ? <> und hat <b className="text-amber-300">{d.inviter.best} Punkte</b> geschafft</> : ""}. Schaffst du mehr?</p>
            </div>
          ) : <p className="text-white/80">Steuere zwei Herzen gleichzeitig – links und rechts getrennt. Sammle Herzen, weich den Dornen aus und führ sie am Treffpunkt zusammen.</p>}
          {d?.enabled === false && <p className="text-amber-200 text-sm font-bold">Das Spiel ist gerade pausiert.</p>}
          <Link to={reg} data-testid="twohearts-register" className="flex items-center justify-center gap-2 h-14 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-lg font-black shadow-[0_10px_30px_rgba(255,60,140,0.45)] active:scale-95 transition-transform">
            <Play className="w-5 h-5" fill="currentColor" /> Kostenlos registrieren & spielen
          </Link>
          <Link to="/login" data-testid="twohearts-login" className="block text-center text-sm font-bold text-white/80">Schon Mitglied? Einloggen</Link>
          {d?.top?.length > 0 && <div className="pt-2"><p className="text-xs font-extrabold text-white/60 mb-2 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> BESTENLISTE</p><Board rows={d.top} testId="twohearts-public-board" /></div>}
        </div>
      </div>
    </div>
  );
}

function Over({ res, code, onAgain, onClose }) {
  const share = () => {
    const text = `Ich habe bei „Zwei Herzen“ ${res.score} Punkte geschafft 💞 Schaffst du mehr?`;
    openShare({ kind: "game", target: "twohearts", text, title: "Freunde herausfordern", msg: text, fallbackUrl: shareLink(code) });
  };
  return (
    <div className="absolute inset-0 z-20 grid place-items-center bg-black/55 backdrop-blur-sm p-5" data-testid="twohearts-over">
      <div className="w-full max-w-sm rounded-[2rem] bg-gradient-to-b from-[#4a0f3a] to-[#2a0b2e] ring-1 ring-pink-400/30 p-6 text-center text-white th-pop">
        <Heart className="w-10 h-10 mx-auto text-pink-400" fill="currentColor" />
        <p className="mt-2 text-xs font-extrabold tracking-[0.2em] text-pink-200">{res.record ? "NEUER REKORD!" : "GAME OVER"}</p>
        <p className="font-display text-6xl font-black tabular-nums" data-testid="twohearts-final-score">{res.score}</p>
        <p className="text-sm text-white/70">Level {res.level} · Bestleistung {res.best}</p>
        {res.today_rank && <p className="mt-1 text-sm font-bold text-amber-300" data-testid="twohearts-rank">Platz {res.today_rank} heute</p>}
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-bold" data-testid="twohearts-earned">
          <Sparkles className="w-4 h-4 text-amber-300" /> +{(res.points || 0) + (res.bonus || 0)} Punkte{res.bonus ? " (inkl. Rekord-Bonus)" : ""} · heute {res.today_points}/{res.daily_cap}
        </p>
        <div className="mt-5 grid gap-2">
          <button onClick={onAgain} data-testid="twohearts-again" className="h-12 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 font-black inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><RotateCcw className="w-5 h-5" /> Nochmal</button>
          <button onClick={share} data-testid="twohearts-share" className="h-12 rounded-full bg-white/10 ring-1 ring-white/20 font-bold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Freunde herausfordern</button>
          <button onClick={onClose} data-testid="twohearts-close" className="h-10 text-sm font-bold text-white/70">Zurück</button>
        </div>
      </div>
    </div>
  );
}

function Member() {
  const [d, setD] = useState(null);
  const [run, setRun] = useState(null);
  const [res, setRes] = useState(null);
  const [tab, setTab] = useState("today");
  const load = () => api.get("/twohearts").then(({ data }) => setD(data)).catch((e) => { setD({ err: formatErr(e) }); });
  useEffect(() => { load(); sessionStorage.removeItem("after_auth"); }, []);
  const start = async () => {
    try { const { data } = await api.post("/twohearts/start"); setRes(null); setRun({ id: data.session_id, k: Date.now() }); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const over = async ({ score, level }) => {
    try { const { data } = await api.post("/twohearts/finish", { session_id: run.id, score, level }); setRes({ ...data, level }); load(); }
    catch (e) { toast.error(formatErr(e)); setRes({ score, level, best: Math.max(score, d?.best || 0), today_points: d?.today_points || 0, daily_cap: d?.daily_cap || 20 }); }
  };
  if (run) {
    return (
      <div className="fixed inset-0 z-[80] bg-[#2a0b2e]" data-testid="twohearts-play">
        <TwoHeartsCanvas key={run.k} onOver={over} skin={skinColors(d)} />
        <div className="absolute top-4 left-4 z-10"><MusicToggle dark compact /></div>
        <button onClick={() => { setRun(null); setRes(null); }} aria-label="Beenden" data-testid="twohearts-quit" className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center"><X className="w-5 h-5" /></button>
        {res && <Over res={res} code={d?.code} onAgain={start} onClose={() => { setRun(null); setRes(null); }} />}
      </div>
    );
  }
  return (
    <div className="min-h-[100dvh] bg-[#2a0b2e] text-white" data-testid="twohearts-lobby">
      <div className="relative h-[46vh] overflow-hidden">
        <TwoHeartsCanvas demo heartY={0.55} testId="twohearts-demo" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2a0b2e] via-transparent to-transparent" />
        <Link to="/spiele" data-testid="twohearts-back" className="absolute top-4 left-4 h-9 px-4 rounded-full bg-white/10 backdrop-blur text-sm font-bold grid place-items-center">← Spiele</Link>
        <div className="absolute top-4 right-4"><MusicToggle dark /></div>
      </div>
      <div className="max-w-md mx-auto px-5 -mt-24 relative space-y-5 pb-28">
        <h1 className="hero-plain font-display text-5xl font-black leading-none">Zwei <span className="text-pink-400">Herzen</span></h1>
        <InviteLock gate="games" dark />
        <p className="text-white/75 text-sm">Tippe links für das pinke Herz und rechts für das goldene. Sammle Herzen, weich den Dornen aus – alle 15 Herzen finden sich beide am Treffpunkt und es wird schneller.</p>
        {d?.err && <p className="text-amber-200 font-bold" data-testid="twohearts-error">{d.err}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-3xl bg-white/10 p-4"><p className="text-[11px] font-extrabold text-white/60">BESTLEISTUNG</p><p className="text-3xl font-black tabular-nums" data-testid="twohearts-best">{d?.best ?? 0}</p></div>
          <div className="rounded-3xl bg-white/10 p-4"><p className="text-[11px] font-extrabold text-white/60">PUNKTE HEUTE</p><p className="text-3xl font-black tabular-nums" data-testid="twohearts-today">{d?.today_points ?? 0}<span className="text-base text-white/50">/{d?.daily_cap ?? 20}</span></p></div>
        </div>
        <button onClick={start} disabled={!d || d.err} data-testid="twohearts-start" className="w-full h-16 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-xl font-black inline-flex items-center justify-center gap-2 shadow-[0_10px_30px_rgba(255,60,140,0.45)] active:scale-95 transition-transform disabled:opacity-50 th-breathe">
          <Play className="w-6 h-6" fill="currentColor" /> Spielen
        </button>
        <button onClick={() => openShare({ kind: "game", target: "twohearts", text: "Spiel mit mir „Zwei Herzen“ 💞", title: "Freunde einladen", msg: "Spiel mit mir „Zwei Herzen“ 💞", fallbackUrl: shareLink(d?.code) })}
          data-testid="twohearts-invite" className="w-full h-12 rounded-full bg-white/10 ring-1 ring-white/15 font-bold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Freunde einladen</button>
        <div>
          <div className="flex gap-2 mb-3">
            {[["today", "Heute"], ["week", "Woche"], ["all", "Allzeit"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`twohearts-tab-${k}`} className={`h-9 px-4 rounded-full text-sm font-bold ${tab === k ? "bg-pink-500" : "bg-white/10 text-white/70"}`}>{l}</button>)}
          </div>
          <Board rows={tab === "today" ? d?.today_top : tab === "week" ? d?.week_top : d?.top} champion={tab === "week" ? d?.champion : null} testId="twohearts-board" />
          {tab === "week" && <p className="mt-2 text-xs text-white/60">Der oder die Wochenbeste trägt live die Krone – am Montag gibt es +50 Punkte.</p>}
        </div>
        {d && !d.err && <DuelPanel />}
        {d?.skins && <SkinShop d={d} reload={load} />}
      </div>
    </div>
  );
}

export default function TwoHearts() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-[100dvh] bg-[#2a0b2e]" />;
  return user ? <Member /> : <Guest />;
}
